import { getProjectWellAndFieldNamesApi } from '@core/core/api'
import type {
  MappedEntityType,
  MarkerSetupType,
  MultipartUploadedFile,
  UploadedFileStepTransitionType,
  UploadedFileStepType,
} from '@core/core/types'
import { defaultUploadedFileStepStatuses } from '@core/core/types'
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import type { ProjectId } from 'common/types'

import type { AppThunk, RootState } from '../../store'
import { isMultipartSetupOfSpecificType } from '../helpers'
import { selectById } from '../multipartUploadedFileSelectors'
import {
  stepsToStatusesMapping,
  transitionToStepsMapping,
  updateMultipartContext,
  updateMultipartState,
} from './common'

export const updateMarkerSetupConfig = (
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  fileId: string,
  setupContent: MarkerSetupType | undefined,
) => {
  if (!setupContent) return

  const setupContentNew = { ...setupContent, isValid: checkSetupContent(setupContent) }
  dispatch(
    updateMultipartState({
      id: fileId,
      changes: {
        setupContent: setupContentNew,
        statuses: {
          ...defaultUploadedFileStepStatuses,
          setup: setupContentNew.isValid ? 'inprogress' : 'failed',
          mapping: 'notstarted',
          loading: 'notstarted',
        },
      },
    }),
  )
}

export const updateMarkerUploadedFileStep = (
  projectId: ProjectId,
  fileId: string,
  step: UploadedFileStepTransitionType,
  file: File,
  entity: MultipartUploadedFile,
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  state: RootState,
) => {
  if (!entity.statuses) return

  const actualStep: UploadedFileStepType =
    transitionToStepsMapping.get(`${entity.step}->${step}`)?.(entity.statuses, entity.entityType) ??
    'setup'
  const statuses = stepsToStatusesMapping.get(`${entity.step}->${actualStep}`)
  dispatch(
    updateMultipartState({
      id: fileId,
      changes: {
        step: actualStep,
        statuses: { ...entity.statuses, ...statuses },
      },
    }),
  )

  if (actualStep === 'mapping') {
    startMapping(dispatch, state, fileId)
  }
}

export const getWellsForMappingForProject =
  (projectId: ProjectId): AppThunk =>
  async dispatch => {
    const wells = await getProjectWellAndFieldNamesApi(projectId)
    dispatch(updateMultipartContext({ wellsForMapping: wells }))
  }

export const setMarkerWellMappings =
  (fileId: string, mappedEntities: MappedEntityType[]): AppThunk =>
  dispatch => {
    dispatch(
      updateMultipartState({
        id: fileId,
        changes: { mappedEntities },
      }),
    )
  }

export const setUpdateIfExists =
  (fileId: string, updateIfExists: boolean): AppThunk =>
  dispatch => {
    dispatch(
      updateMultipartState({
        id: fileId,
        changes: { updateIfExists },
      }),
    )
  }

export const updateMarkerWellMappings =
  (fileId: string, mappedEntities: MappedEntityType[]): AppThunk =>
  (dispatch, getState) => {
    const entity = selectById(getState(), fileId)
    if (!entity?.mappedEntities) return

    const newMappedEntities: MappedEntityType[] = entity.mappedEntities.map(oldEntity => {
      const mappedEntity = mappedEntities.find(entity => entity.name === oldEntity.name)
      return mappedEntity ?? oldEntity
    })

    dispatch(
      updateMultipartState({
        id: fileId,
        changes: { mappedEntities: newMappedEntities },
      }),
    )
  }

export const addMarkerMultipartsToProject = (
  projectId: ProjectId,
  fileId: string,
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  entity: MultipartUploadedFile,
) => {
  if (
    entity.entityType === 'marker' &&
    isMultipartSetupOfSpecificType<MarkerSetupType>(entity, entity.setupContent, 'marker')
  ) {
    try {
      const statuses = stepsToStatusesMapping.get(`${entity.step}->loading`)
      dispatch(
        updateMultipartState({
          id: fileId,
          changes: {
            progress: 0,
            step: 'loading',
            statuses: { ...entity.statuses, ...statuses },
          },
        }),
      )
    } catch (error) {
      dispatch(
        updateMultipartState({
          id: fileId,
          changes: {
            statuses: { ...entity.statuses, loading: 'failed' },
            error: error?.toString(),
          },
        }),
      )
    }
  }
}

const checkSetupContent = (setupContent: MarkerSetupType | undefined) => {
  if (!setupContent) {
    return false
  }
  return !!setupContent.markerBoreDepthColumn
}

const startMapping = (
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  state: RootState,
  fileId: string,
) => {
  const entity = selectById(state, fileId)
  if (!entity?.setupContent) {
    throw new Error('cube params undefined')
  }
  try {
    if (isMultipartSetupOfSpecificType<MarkerSetupType>(entity, entity.setupContent, 'marker')) {
      //await startWellMapping(uploadId, entity.setupContent)
    }
  } catch (error) {
    dispatch(
      updateMultipartState({
        id: fileId,
        changes: {
          statuses: { ...entity.statuses, conversion: 'failed' },
          error: error?.toString(),
        },
      }),
    )
  }
}
